import { inject, Injectable } from '@angular/core';
import { defaultIfEmpty, firstValueFrom } from 'rxjs';
import { AccountService } from '@core/services/account';
import { FeatureFlagsService } from '@core/services/feature-flags.service';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { XAlertService } from '@core/services/x-alert.service';
import { WorkflowsTranslations } from '@core/types';
import { WorkflowPendingWorkService } from './workflow-pending-work.service';

/**
 * Предупреждение при явном выходе, если на устройстве есть неотправленные задачи регламентов.
 * Задачи не удаляются: они отправятся, когда этот пользователь снова войдёт.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowLogoutGuardService {
  private readonly accountService = inject(AccountService);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly pendingWorkService = inject(WorkflowPendingWorkService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  /**
   * Явный выход пользователя: при неотправленных задачах сначала спрашиваем подтверждение.
   * Возвращает true, если выход выполнен.
   */
  async logout(): Promise<boolean> {
    if (!(await this.confirmLogout())) {
      return false;
    }

    this.accountService.logout();

    return true;
  }

  /**
   * true — можно выходить (задач нет или пользователь подтвердил выход)
   */
  async confirmLogout(): Promise<boolean> {
    if (!this.featureFlags.workflowsEnabled) {
      return true;
    }

    const pendingCount = await this.getPendingCount();

    if (pendingCount === 0) {
      return true;
    }

    const t = (key: WorkflowsTranslations, params?: Record<string, unknown>) =>
      this.outkeepTranslateService.instant(key, params);

    return firstValueFrom(
      this.xAlertService
        .showDialog(
          t(WorkflowsTranslations.LogoutUnsentTitle),
          t(WorkflowsTranslations.LogoutUnsentMessage, { count: pendingCount }),
          t(WorkflowsTranslations.LogoutUnsentConfirm),
          t(WorkflowsTranslations.LogoutUnsentCancel),
        )
        .pipe(defaultIfEmpty(false)),
    );
  }

  private async getPendingCount(): Promise<number> {
    try {
      return await this.pendingWorkService.countForOwner();
    } catch (error) {
      // Не удалось прочитать очереди — выход не блокируем
      console.error('Failed to count pending workflow tasks:', error);

      return 0;
    }
  }
}
